import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { ConfigService } from '../../config.service';
import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { downloadFile } from '../../shared/browser.utils';
import { convertMbToBytes } from '../../shared/file.utils';
import {
  DownloadRequest,
  DownloadSummary,
  FileTypeAggregation,
} from '../../shared/models/download.model';

export interface DialogData {
  requestBody: DownloadRequest
}

export interface FileTypeAggregationSelectable extends FileTypeAggregation {
  selected: boolean;
  name: string;
}

@Component({
  host: {
    class: 'mat-dialog-component',
  },
  selector: 'file-download-dialog',
  templateUrl: './file-download-dialog.component.html',
  styleUrls: ['./file-download-dialog.component.scss'],
})
export class FileDownloadDialog {
  protected readonly convertMbToBytes = convertMbToBytes;

  types: FileTypeAggregationSelectable[] = [];
  total: { files: number, size: number } = null
  error: Error = null

  constructor(
    private config: ConfigService,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    public ref: MatDialogRef<FileDownloadDialog>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
  ) {
    this.coreApi.downloads.summary(this.data.requestBody, { type: 'classic' }).subscribe(summary => {
      this.error = null;
      this.types = this.createTypesArray(summary);
      this.calculateTotals();
    });
  }

  createTypesArray(objects: DownloadSummary): FileTypeAggregationSelectable[] {
    let types = [];

    for (const key in objects) {
      if (!Object.prototype.hasOwnProperty.call(objects, key)) {
        return [];
      }

      const object = objects[key] as FileTypeAggregationSelectable;
      if (object.count) {
        object.name = key;
        object.selected = false;
        if (object.name === 'null' || object.name === 'None') {
          object._id = object.name; // to send null or None respectively
          object.name = 'Other';
        }
        types.push(object);
      }
    }
    types = types.sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();

      if (aName < bName) {
        return -1;
      } else if (aName > bName) {
        return 1;
      }
      return 0;
    });
    return types;
  }

  calculateTotals(): void {
    this.total = { files: 0, size: 0 };
    const selectedTypes = this.types.filter(v => v.selected);

    selectedTypes.forEach(type => {
      this.total.size += type.mb_total;
      this.total.files += type.count;
    });
  }

  toggleSelected(type: FileTypeAggregationSelectable): void {
    type.selected = !type.selected;
    this.calculateTotals();
  }

  selectAll(): void {
    this.types.forEach(type => {
      type.selected = true;
    });
    this.calculateTotals();
  }

  selectNone(): void {
    this.types.forEach(type => {
      type.selected = false;
    });
    this.calculateTotals();
  }

  close(): void {
    this.ref.close();
  }

  download(): void {
    const selectedTypes = this.types.filter(v => v.selected);
    const types = selectedTypes.map(type => {
      return [null, 'null', 'None'].includes(type._id) ? 'null' : type._id;
    });

    const downloadRequest: DownloadRequest = {
      ...this.data.requestBody,
      filters: [{
        types: {
          '+': types,
        },
      }],
      optional: true,
      prefix: 'flywheel',
    };

    this.coreApi.downloads.createTicket(downloadRequest, { type: 'classic' }).subscribe({
      next: ({ filename, ticket }) => {
        downloadFile(filename, this.config.getApiUrl(`/api/download?ticket=${ticket}&format=zip`));
      },
      error: error => {
        this.error = error;
        this.logger.error(error);
        this.notifications.error('download.files.error');
      },
    });

    this.close();
  }
}
